<template>
  <v-dialog
    v-model="internalVisible"
    data-cy="certificate-preview-dialogue"
    :content-class="dialogContentClass"
    :transition="isSmallScreen ? 'dialog-bottom-transition' : 'dialog-transition'"
    :aria-labelledby="titleId"
    role="dialog"
    aria-modal="true"
  >
    <v-card flat :loading="loading" class="certificate-preview-card">
      <template #loader="{ isActive }">
        <v-progress-linear
          :active="isActive"
          color="primary"
          height="4"
          indeterminate
        />
      </template>
      <v-card-title class="d-flex align-center bg-white certificate-preview-header">
        <h2 :id="titleId" class="text-break certificate-preview-title">
          {{ t('summary.certificatePreview') }}
        </h2>
        <v-spacer />
        <v-btn
          icon
          variant="text"
          class="certificate-preview-close-btn"
          data-cy="certificate-preview-dialog-close-btn"
          :aria-label="t('certificatePreview.dialogCloseLabel')"
          @click="close"
        >
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <div class="certificate-preview-scrollable">
        <v-alert v-if="showErrorMessage" type="error" dense border="start" colored-border>
          {{ t('certificatePreview.previewLoadError') }}
        </v-alert>
        <template v-else>
          <div class="certificate-preview-stage">
            <img
              v-if="src"
              class="certificate-preview-image"
              :class="{ 'is-ready': !loading }"
              data-cy="certificate-preview-image"
              :alt="t('certificateDetails.previewCertificateAlt')"
              :src="src"
              @load="onLoad"
              @error="onError"
            />
            <v-btn
              v-if="src && !loading"
              size="small"
              variant="flat"
              class="enlarge-preview-btn"
              data-cy="certificate-preview-enlarge-btn"
              @click="toggleEnlarge"
            >
              <v-icon size="18" class="mr-2">{{ isEnlarged ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
              {{ isEnlarged ? t('certificatePreview.shrinkLabel') : t('certificatePreview.enlargeLabel') }}
            </v-btn>
            <div v-if="loading" class="certificate-loading">
              <v-progress-circular indeterminate color="primary" :size="60" :width="6" />
              <p class="mt-4 text-gray-600">
                {{ t('summary.loadingCertificate') }}
              </p>
            </div>
          </div>
        </template>
      </div>

      <div class="certificate-preview-footer text-center">
        <v-btn
          variant="text"
          class="close-preview-btn"
          data-cy="certificate-preview-continue-editing-btn"
          :aria-label="t('summary.giftCard.continueEditing')"
          @click="continueEditing"
        >
          <v-icon size="18" class="mr-2">mdi-pencil</v-icon>
          {{ t('summary.giftCard.continueEditing') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useLabel } from '@/composables/useLabel';
import { useCertificateOperations } from '@/composables/useCertificateOperations';
import { DonationCertificate } from '@/interfaces/donation/certificate/donationCertificate';
import store from '@/store';
import { Donation } from '@/interfaces/donation/donation';
import { RequestSession } from '@/interfaces/requestSession';
import { Intermediary } from '@/interfaces/intermediary/intermediary';

const previewSrcCache = new Map<string, string>();
const previewInflight = new Map<string, Promise<string>>();

export default defineComponent({
  name: 'CertificatePreview',
  props: {
    modelValue: { type: Boolean, required: true },
    imageId: { type: [Number, String, null], required: false },
    imageUrl: { type: String, default: '' },
    imageKey: { type: String, default: '' },
    subjectName: { type: String, required: true },
    donationCauseId: { type: Number, required: true },
    message: { type: String, default: '' },
    donorNames: { type: String, default: '' },
  },
  emits: ['update:modelValue', 'close', 'edit'],
  setup(props, { emit }) {
    const { t } = useLabel();
    const { generateCertificate } = useCertificateOperations();
    const internalVisible = ref(props.modelValue);
    const loading = ref(false);
    const showErrorMessage = ref(false);
    const src = ref('');
    const errorCount = ref(0);
    const maxErrors = ref(3);
    const titleId = `gift-card-preview-title-${Math.random().toString(36).slice(2, 9)}`;
    const isSmallScreen = ref(false);
    const isEnlarged = ref(false);

    const syncViewport = () => {
      isSmallScreen.value = typeof window !== 'undefined' && window.innerWidth <= 600;
    };

    onMounted(() => {
      syncViewport();
      window.addEventListener('resize', syncViewport);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', syncViewport);
    });

    const dialogContentClass = computed(() => {
      let classes = isSmallScreen.value
        ? 'certificate-preview-dialog certificate-preview-dialog--sheet'
        : 'certificate-preview-dialog';
      if (isEnlarged.value) {
        classes += ' certificate-preview-dialog--enlarged';
      }
      return classes;
    });

    const toggleEnlarge = () => {
      isEnlarged.value = !isEnlarged.value;
    };

    const intermediary = computed((): Intermediary | null => {
      const session = store.state.requestSession as RequestSession | null;
      const donation = store.state.donation as Donation | null;
      return session?.intermediary || donation?.donationCase?.intermediary || null;
    });

    const intermediaryLogoUrl = computed(() => (intermediary.value?.logoUrl || '').trim());
    const intermediaryName = computed(() => (intermediary.value?.name || '').trim());

    const payloadStamp = computed(() => {
      return JSON.stringify({
        imageId: props.imageId || 'no-theme',
        imageUrl: props.imageKey ? '' : props.imageUrl || '',
        imageKey: props.imageKey || '',
        subjectName: props.subjectName,
        donationCauseId: props.donationCauseId,
        message: props.message,
        donorNames: props.donorNames?.trim() || '',
        intermediaryLogoUrl: intermediaryLogoUrl.value,
        intermediaryName: intermediaryName.value,
      });
    });

    watch(
      () => props.modelValue,
      (visible: boolean) => {
        internalVisible.value = visible;
      }
    );

    watch(internalVisible, (visible: boolean) => {
      emit('update:modelValue', visible);
      if (visible) {
        loadPreview();
      } else {
        isEnlarged.value = false;
        emit('close');
      }
    });

    watch(payloadStamp, (newStamp: string, oldStamp: string) => {
      if (internalVisible.value && newStamp !== oldStamp) {
        loadPreview();
      }
    });

    const close = () => {
      internalVisible.value = false;
    };

    const continueEditing = () => {
      internalVisible.value = false;
      emit('edit');
    };

    const certificatePayload = (imageId: number): DonationCertificate => {
      const payload: DonationCertificate = {
        imageId: props.imageKey || props.imageUrl ? 0 : imageId,
        imageUrl: props.imageKey ? '' : props.imageUrl || '',
        imageKey: props.imageKey || undefined,
        deceasedName: props.subjectName,
        donationCauseId: props.donationCauseId,
        message: props.message,
        donorNames: props.donorNames?.trim() || '',
        source: '',
      };
      if (intermediaryName.value) {
        payload.intermediaryName = intermediaryName.value;
      }
      if (intermediaryLogoUrl.value) {
        payload.intermediaryLogoUrl = intermediaryLogoUrl.value;
      }
      return payload;
    };

    const previewCacheKey = (stamp: string) => `jpeg:${stamp}`;

    const buildPreviewSrc = async (): Promise<string> => {
      const imageId = typeof props.imageId === 'number' ? props.imageId : 0;
      const res = await generateCertificate(certificatePayload(imageId || 1));
      return `${res.donationCertificate.source}?asImage=true`;
    };

    const resolvePreviewSrc = (stamp: string): Promise<string> => {
      const key = previewCacheKey(stamp);
      const cached = previewSrcCache.get(key);
      if (cached) {
        return Promise.resolve(cached);
      }

      const inflight = previewInflight.get(key);
      if (inflight) {
        return inflight;
      }

      const request = buildPreviewSrc()
        .then((url) => {
          previewSrcCache.set(key, url);
          previewInflight.delete(key);
          return url;
        })
        .catch((err) => {
          previewInflight.delete(key);
          throw err;
        });

      previewInflight.set(key, request);
      return request;
    };

    const loadPreview = async (force = false) => {
      const stamp = payloadStamp.value;
      const key = previewCacheKey(stamp);
      if (force) {
        previewSrcCache.delete(key);
        previewInflight.delete(key);
      }

      const cached = previewSrcCache.get(key);
      if (cached) {
        src.value = cached;
        loading.value = false;
        showErrorMessage.value = false;
        return;
      }

      loading.value = true;
      showErrorMessage.value = false;
      src.value = '';

      try {
        const url = await resolvePreviewSrc(stamp);
        if (stamp !== payloadStamp.value) {
          return;
        }
        src.value = url;
      } catch {
        if (stamp !== payloadStamp.value) {
          return;
        }
        showErrorMessage.value = true;
        loading.value = false;
      }
    };

    if (internalVisible.value) {
      loadPreview();
    }

    const onLoad = () => {
      loading.value = false;
      errorCount.value = 0;
    };

    const onError = () => {
      loading.value = false;
      errorCount.value++;
      previewSrcCache.delete(previewCacheKey(payloadStamp.value));
      if (errorCount.value <= maxErrors.value) {
        loadPreview(true);
      } else {
        showErrorMessage.value = true;
      }
    };

    return {
      t,
      internalVisible,
      titleId,
      loading,
      showErrorMessage,
      src,
      isSmallScreen,
      isEnlarged,
      dialogContentClass,
      close,
      continueEditing,
      toggleEnlarge,
      onLoad,
      onError,
    };
  },
});
</script>

<style scoped>
.certificate-preview-card {
  display: flex;
  flex-direction: column;
  width: min(calc(100vw - 32px), calc((100dvh - 200px) * 210 / 297 + 32px));
  max-width: 100%;
  overflow: hidden;
  border: none !important;
  border-radius: 16px !important;
  box-shadow: none !important;
  background-color: #fff !important;
}

.certificate-preview-header {
  flex-shrink: 0;
  padding: 12px 16px 12px 20px !important;
  min-height: 52px;
  border-bottom: 1px solid #e5e7eb;
}

.certificate-preview-title {
  margin: 0;
  font-family: 'Lato', sans-serif !important;
  font-size: 16px !important;
  font-weight: 800 !important;
  line-height: 29px !important;
  color: #1f2a37 !important;
  min-width: 0;
}

.certificate-preview-scrollable {
  flex: 0 0 auto;
  background-color: white !important;
  padding: 16px;
}

.certificate-preview-stage {
  position: relative;
  width: 100%;
  aspect-ratio: 210 / 297;
  background-color: #fff;
}

.certificate-preview-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: 0;
}

.certificate-preview-image.is-ready {
  opacity: 1;
}

.certificate-loading {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  padding: 24px;
}

.certificate-preview-footer {
  flex-shrink: 0;
  border-top: 1px solid #e5e7eb;
  padding: 16px 16px calc(16px + env(safe-area-inset-bottom, 0px));
  background-color: white !important;
}

.certificate-preview-close-btn {
  background: white;
  border: none;
  box-shadow: none;
}

.certificate-preview-close-btn:hover,
.certificate-preview-close-btn:focus,
.certificate-preview-close-btn:focus-visible {
  box-shadow: none;
}

.certificate-preview-close-btn:focus-visible {
  outline: 2px solid #047481 !important;
  outline-offset: 2px;
}

.close-preview-btn {
  text-transform: uppercase !important;
  font-family: 'Lato', sans-serif !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px;
  color: #374151 !important;
  outline: none !important;
  box-shadow: none !important;
}

.close-preview-btn:hover,
.close-preview-btn:focus {
  background-color: #f3f4f6 !important;
  box-shadow: none !important;
}

.close-preview-btn:focus-visible {
  outline: 2px solid #047481 !important;
  outline-offset: 2px;
  box-shadow: none !important;
}

.enlarge-preview-btn {
  position: absolute;
  z-index: 2;
  text-transform: uppercase !important;
  font-family: 'Lato', sans-serif !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  letter-spacing: 0.5px;
  color: #047481 !important;
  background-color: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid #d1d5db !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.12) !important;
}

.enlarge-preview-btn:hover {
  background-color: #ffffff !important;
}

.enlarge-preview-btn:focus-visible {
  outline: 2px solid #047481 !important;
  outline-offset: 2px;
}
</style>

<style>
.certificate-preview-dialog {
  width: auto !important;
  max-width: calc(100vw - 32px) !important;
  height: auto !important;
  max-height: calc(100dvh - 32px) !important;
  margin: 16px !important;
  display: flex !important;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px !important;
  background-color: #fff;
  box-shadow: none !important;
}

.certificate-preview-dialog .certificate-preview-card {
  width: min(calc(100vw - 32px), calc((100dvh - 200px) * 210 / 297 + 32px));
  max-width: 100%;
  height: auto;
}

.certificate-preview-dialog--sheet {
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  top: auto !important;
  max-height: 100dvh !important;
  border-radius: 16px 16px 0 0 !important;
}

.certificate-preview-dialog--sheet .certificate-preview-card {
  width: 100%;
  max-width: 100%;
  max-height: 100dvh;
  border-radius: 16px 16px 0 0 !important;
}

.certificate-preview-dialog--sheet .certificate-preview-scrollable {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.certificate-preview-dialog--sheet .certificate-preview-stage {
  width: min(100%, calc((100dvh - 180px) * 210 / 297));
}

.certificate-preview-dialog--sheet .certificate-preview-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
}

.certificate-preview-dialog--enlarged {
  width: 100vw !important;
  max-width: 100vw !important;
  height: 100dvh !important;
  max-height: 100dvh !important;
  margin: 0 !important;
  left: 0 !important;
  right: 0 !important;
  top: 0 !important;
  bottom: 0 !important;
  border-radius: 0 !important;
}

.certificate-preview-dialog--enlarged .certificate-preview-card {
  width: 100%;
  max-width: 100%;
  height: 100dvh;
  border-radius: 0 !important;
}

.certificate-preview-dialog--enlarged .certificate-preview-scrollable {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 24px;
  overflow: auto;
}

/* Auto margins centre the page only when there is spare room and collapse to 0
   when it overflows; centring instead would put the left edge at a negative
   scroll offset the container can never reach. */
.certificate-preview-dialog--enlarged .certificate-preview-stage {
  width: 800px;
  flex-shrink: 0;
  margin-inline: auto;
}
</style>
